import { useEffect, useId, useRef } from 'react';
import type {
  ButtonHTMLAttributes,
  ComponentProps,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

// ---------------------------------------------------------------------------
// Hand-rolled primitives over the token CSS in `theme.css`. No UI kit on
// purpose: see STYLEGUIDE.md ("Why no component library").
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, size === 'sm' ? 'btn--sm' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={classes} disabled={disabled ?? loading} {...rest}>
      {loading ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------

export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {required ? (
          <span className="field__required" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {children}
      {hint && !error ? <p className="field__hint">{hint}</p> : null}
      {error ? (
        <p className="field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * `ComponentProps<'input'>`, not `InputHTMLAttributes`: React 19 passes `ref`
 * through as a plain prop, and the code field of the e-mail login (T.Y8) needs to
 * move the focus into it — the type has to admit that.
 */
export function Input({ className, ...rest }: ComponentProps<'input'>) {
  return <input className={['input', className ?? ''].filter(Boolean).join(' ')} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={['input', className ?? ''].filter(Boolean).join(' ')} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={['input', className ?? ''].filter(Boolean).join(' ')} {...rest}>
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------

/** Inline status pill. The colour comes from the status semantics, not from us. */
export function StatusPill({ status, title }: { status: string; title?: string }) {
  return (
    <span className={`pill pill--${status.toLowerCase()}`} title={title}>
      {status}
    </span>
  );
}

export function Badge({
  children,
  tone = 'neutral',
  title,
  testId,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'warn' | 'ok' | 'bad';
  title?: string;
  /**
   * A4/T.Z19 (2026-09-13) — the one hook a spec may pin a badge by. The profile's
   * flag badges documented `profile-flag-…` testids for waves without ever being
   * able to match one, so their «absent» assertions passed vacuously; the prop is
   * optional, so every existing call site is untouched.
   */
  testId?: string;
}) {
  return (
    <span className={`badge badge--${tone}`} title={title} data-testid={testId}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------

/** Closes on Escape; `role="dialog"` + focus moved inside on open. */
function useOverlay(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const labelId = useId();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      // Minimal focus trap: keep Tab cycling inside the overlay.
      if (event.key !== 'Tab' || !ref.current) return;
      const focusable = ref.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const timer = window.setTimeout(() => {
      ref.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      window.clearTimeout(timer);
    };
  }, [open, onClose]);

  return { ref, labelId };
}

interface OverlayProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** ADDITIVE (T.Z63, T.Z76) — the wide variant (`.modal--wide` / `.drawer--wide`):
   *  a report that carries hundreds of rows needs the room the default 720px dialog
   *  does not have, and the prefill row drawer needs more than 560px for its
   *  «Программа» Combobox. Purely presentational, off by default. */
  wide?: boolean;
}

/**
 * T.Z76 — the width variants of the two overlays, one fold instead of the two
 * ternaries it replaces (the modal has carried `--wide` since T.Z63, the drawer
 * answers it now): `overlayClass('drawer', true)` is `drawer drawer--wide`, and the
 * plain name alone means the default width. Pure so the plain-Node `unit` project
 * can pin it — the components themselves cannot be server-rendered there (the
 * runner's own JSX markers, see `prefill-import-table.unit.spec.ts`).
 */
export function overlayClass(base: string, wide: boolean): string {
  return wide ? `${base} ${base}--wide` : base;
}

export function Modal({ open, onClose, title, description, children, footer, wide = false }: OverlayProps) {
  const { ref, labelId } = useOverlay(open, onClose);
  if (!open) return null;

  return (
    <div className="overlay" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        className={overlayClass('modal', wide)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        ref={ref}
      >
        <header className="modal__header">
          <div>
            <h2 className="modal__title" id={labelId}>
              {title}
            </h2>
            {description ? <p className="modal__desc">{description}</p> : null}
          </div>
          <button type="button" className="icon-btn" aria-label="Закрыть диалог" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function Drawer({ open, onClose, title, description, children, footer, wide = false }: OverlayProps) {
  const { ref, labelId } = useOverlay(open, onClose);
  if (!open) return null;

  return (
    <div
      className="overlay overlay--right"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      {/* T.Z76 — `wide` lifts the drawer to the import modal's 1080px (`.drawer--wide`):
          the prefill form's «Программа» Combobox did not fit the default 560px and
          overflowed it. Off by default — every other drawer here is a short form or a
          read-only panel and stays at the 560px the shell was designed around. */}
      <aside
        className={overlayClass('drawer', wide)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        ref={ref}
      >
        <header className="modal__header">
          <div>
            <h2 className="modal__title" id={labelId}>
              {title}
            </h2>
            {description ? <p className="modal__desc">{description}</p> : null}
          </div>
          <button type="button" className="icon-btn" aria-label="Закрыть панель" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="drawer__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------

/** The three mandatory states of every data surface (see STYLEGUIDE.md §6). */

export function Spinner({ label = 'Загрузка' }: { label?: string }) {
  return (
    <div className="state state--loading" role="status" aria-live="polite">
      <span className="spinner spinner--lg" aria-hidden="true" />
      <span>{label}…</span>
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      {hint ? <p className="state__hint">{hint}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">Что-то пошло не так</p>
      <p className="state__hint">{message}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Повторить
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function Pagination({
  meta,
  onPage,
  rangeCaption,
}: {
  meta: { page: number; totalPages: number; total: number; limit: number };
  onPage: (page: number) => void;
  /**
   * ADDITIVE (T.Z119) — the «Показаны X–Y из N» caption, in the words the table's own
   * note uses. When given it REPLACES the bare «Страница … · всего …» label (the page
   * count it carried rides along in the caption's place), and the footer then renders
   * even on a single page — a one-page table still deserves its honest count. Callers
   * that pass nothing keep the old label and the old hide-on-one-page rule exactly.
   */
  rangeCaption?: string;
}) {
  const singlePage = meta.totalPages <= 1;
  if (singlePage && rangeCaption === undefined) return null;
  return (
    <div className="pagination" data-testid="pagination">
      <span data-testid="pagination-label">
        {rangeCaption ?? (
          <>
            Страница {meta.page} из {meta.totalPages} · всего {meta.total}
          </>
        )}
      </span>
      <span className="pagination__pages">
        <Button size="sm" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}>
          ← Назад
        </Button>
        <Button size="sm" disabled={meta.page >= meta.totalPages} onClick={() => onPage(meta.page + 1)}>
          Вперёд →
        </Button>
      </span>
    </div>
  );
}
